import { createEmitter } from 'utils/emitter';
import { resetAmbossSessionState } from './ambossSessionState';
import { triggerAmbossDataReset } from './ambossDataReset';
import { AMBOSS_HISTORY_STORAGE_KEY } from './useAmbossHistory';

export type AmbossDataGatePhase = 'pending' | 'ready' | 'blocked';

const AMBOSS_DATA_OWNER_KEY = 'ambossDataOwner';

let phase: AmbossDataGatePhase = 'pending';
let activeOwnerStamp: string | null = null;
let runToken = 0;
const gateEmitter = createEmitter();

function setPhase(next: AmbossDataGatePhase): void {
  if (phase === next) return;
  phase = next;
  gateEmitter.notify();
}

export const subscribeToAmbossDataGate = gateEmitter.subscribe;

export function getAmbossDataGatePhase(): AmbossDataGatePhase {
  return phase;
}

async function evaluate(previousOwnerStamp: string | null = null): Promise<void> {
  const token = ++runToken;
  const ownerStamp = activeOwnerStamp;
  if (!ownerStamp) {
    setPhase('pending');
    return;
  }
  const api = window.electronAPI;
  if (!api) {
    if (previousOwnerStamp !== null) {
      window.localStorage.removeItem(AMBOSS_HISTORY_STORAGE_KEY);
      resetAmbossSessionState();
      triggerAmbossDataReset();
    }
    setPhase('ready');
    return;
  }
  setPhase('pending');
  try {
    const owner = await api.versions?.get?.(AMBOSS_DATA_OWNER_KEY);
    if (token !== runToken) return;
    if (owner === ownerStamp) {
      setPhase('ready');
      return;
    }
    window.localStorage.removeItem(AMBOSS_HISTORY_STORAGE_KEY);
    resetAmbossSessionState();
    triggerAmbossDataReset();
    const wiped = (await api.clearAmbossData?.()) === true;
    if (token !== runToken) return;
    if (!wiped) {
      setPhase('blocked');
      return;
    }
    await api.versions?.set?.(AMBOSS_DATA_OWNER_KEY, ownerStamp);
    if (token !== runToken) return;
    setPhase('ready');
  } catch (error) {
    console.error('AMBOSS data gate evaluation failed', error);
    if (token === runToken) setPhase('blocked');
  }
}

export function setAmbossDataGateUser(email: string | null): void {
  const ownerStamp = email ? email.trim().toLowerCase() : null;
  if (ownerStamp === activeOwnerStamp) return;
  const previousOwnerStamp = activeOwnerStamp;
  activeOwnerStamp = ownerStamp;
  void evaluate(previousOwnerStamp);
}

export function retryAmbossDataGate(): void {
  if (phase === 'blocked') void evaluate();
}
